import { useState, useCallback, useRef } from 'react';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { ChevronRightIcon } from '@/components/ui/Icons';
import {
  PROJECT_STATUSES,
  STATUS_ICONS,
  SUBSTATUSES,
  STATUSES_WITH_SUBMENU,
  getFullStatus,
  getBaseStatus,
  getSubstatus,
  isCompletedStatus,
} from '@/utils/projectStatuses';
import type { ProjectStatus } from '@/utils/projectStatuses';
import { highlightPlainText } from '@/utils/highlightSearchTerms';
import './StateCell.css';

interface StateCellProps {
  value: string;
  projectId: string;
  onChange: (projectId: string, newState: string) => void;
  disabled?: boolean;
  searchHighlightWords?: string[];
}

const SUBMENU_CLOSE_DELAY_MS = 150;

export function StateCell({
  value,
  projectId,
  onChange,
  disabled,
  searchHighlightWords,
}: StateCellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [submenuStatus, setSubmenuStatus] = useState<string | null>(null);
  const [submenuEl, setSubmenuEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const baseValue = getBaseStatus(value);
  const substatus = getSubstatus(value);
  const displayValue = substatus ?? (value || 'Добавлен');
  const StatusIcon = STATUS_ICONS[baseValue as ProjectStatus] ?? STATUS_ICONS.Добавлен;

  const handleButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      setMenuAnchorEl(e.currentTarget);
      setMenuOpen((prev) => !prev);
      setSubmenuStatus(null);
      setSubmenuEl(null);
    },
    [disabled]
  );

  const handleSelect = useCallback(
    (newState: string) => {
      onChange(projectId, newState);
      setMenuOpen(false);
      setMenuAnchorEl(null);
      setSubmenuStatus(null);
      setSubmenuEl(null);
    },
    [projectId, onChange]
  );

  const hasSubmenu = (status: string) =>
    STATUSES_WITH_SUBMENU.includes(status as (typeof STATUSES_WITH_SUBMENU)[number]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setSubmenuStatus(null);
      setSubmenuEl(null);
      closeTimerRef.current = null;
    }, SUBMENU_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const cancelSubmenuClose = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  return (
    <>
      <div className="projects-table__state-cell-inner">
        <button
          type="button"
          className={`projects-table__action-btn projects-table__state-btn ${isCompletedStatus(value) ? 'projects-table__state-btn_complete' : ''}`}
          onClick={handleButtonClick}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-label={`Состояние: ${value || 'Добавлен'}`}
        >
          <StatusIcon sx={{ fontSize: 14 }} className="projects-table__state-btn-icon" />
          <span className="projects-table__state-btn-text">
            {searchHighlightWords?.length
              ? highlightPlainText(displayValue, searchHighlightWords)
              : displayValue}
          </span>
          <ChevronRightIcon size={12} className="projects-table__state-btn-chevron" />
        </button>
      </div>
      <ContextMenu
        isOpen={menuOpen}
        onClose={() => {
          setMenuOpen(false);
          setMenuAnchorEl(null);
          setSubmenuStatus(null);
          setSubmenuEl(null);
        }}
        anchorPosition={null}
        anchorEl={menuAnchorEl}
        alignY="center"
      >
        <ul className="context-menu__list context-menu__list_state" role="listbox">
          {PROJECT_STATUSES.map((status) => {
            const Icon = STATUS_ICONS[status as ProjectStatus];
            const subs = SUBSTATUSES[status];
            const isSelected =
              status === displayValue ||
              (subs && subs.some((s) => getFullStatus(status, s) === value));
            const showSubmenu = hasSubmenu(status) && submenuStatus === status;

            return (
              <li
                key={status}
                role="option"
                className="context-menu__state-item"
                onMouseEnter={(e) => {
                  if (hasSubmenu(status)) {
                    cancelSubmenuClose();
                    setSubmenuStatus(status);
                    setSubmenuEl(e.currentTarget);
                  }
                }}
                onMouseLeave={() => {
                  if (hasSubmenu(status)) scheduleSubmenuClose();
                }}
              >
                {subs ? (
                  <>
                    <button
                      type="button"
                      className={`context-menu__action context-menu__action_with-sub ${isSelected ? 'context-menu__action_selected' : ''}`}
                      onClick={() => handleSelect(status)}
                      role="menuitem"
                    >
                      <Icon sx={{ fontSize: 18 }} />
                      <span className="context-menu__action-text">{status}</span>
                      <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                    </button>
                    {showSubmenu && submenuEl && (
                      <ContextMenuSubmenu
                        anchorEl={submenuEl}
                        onMouseEnter={cancelSubmenuClose}
                        onMouseLeave={() => {
                          setSubmenuStatus(null);
                          setSubmenuEl(null);
                        }}
                      >
                        <ul className="context-menu__list" role="listbox">
                          {subs.map((sub) => {
                            const full = getFullStatus(status, sub);
                            return (
                              <li key={full} role="option">
                                <button
                                  type="button"
                                  className={`context-menu__action ${full === value ? 'context-menu__action_selected' : ''}`}
                                  onClick={() => handleSelect(full)}
                                  role="menuitem"
                                >
                                  {sub}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </ContextMenuSubmenu>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className={`context-menu__action ${isSelected ? 'context-menu__action_selected' : ''}`}
                    onClick={() => handleSelect(status)}
                    role="menuitem"
                  >
                    <Icon sx={{ fontSize: 18 }} />
                    {status}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </ContextMenu>
    </>
  );
}
